import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Toaster } from "../../../common/Toaster/Toaster";
import Loader from "../../../common/Loader/Loader";
import { fetchExecutiveAnalytics } from "../../../service/SubscriptionWellDhanService";
import { TrendingUp } from "lucide-react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip);

const ResizableChart = ({ label, children, minHeight = 200 }) => {
  const cardRef = useRef(null);
  const [height, setHeight] = useState(minHeight);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    const scrollEl = cardRef.current.closest("main") ?? cardRef.current.closest(".overflow-y-auto");
    const getScrollTop = () => scrollEl?.scrollTop ?? 0;
    const startScrollTop = getScrollTop();
    const startClientY = e.clientY;
    const startH = cardRef.current.getBoundingClientRect().height;
    let currentClientY = e.clientY;
    let raf = null;

    const tick = () => {
      // auto-scroll when cursor within 80px of bottom edge
      const containerRect = scrollEl
        ? scrollEl.getBoundingClientRect()
        : { bottom: window.innerHeight };
      const distFromBottom = containerRect.bottom - currentClientY;
      if (distFromBottom < 80 && distFromBottom > 0) {
        const speed = Math.round((80 - distFromBottom) / 4);
        scrollEl.scrollBy({ top: speed });
      }
      // height = original height + (how far cursor moved down) + (how much page scrolled)
      const scrollDelta = getScrollTop() - startScrollTop;
      const newH = Math.max(minHeight, startH + (currentClientY - startClientY) + scrollDelta);
      setHeight(newH);
      raf = requestAnimationFrame(tick);
    };

    const onMove = (mv) => { currentClientY = mv.clientY; };
    const onUp = () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    raf = requestAnimationFrame(tick);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [minHeight]);

  return (
    <div
      ref={cardRef}
      className="bg-white rounded-xl border border-gray-200 px-4 pt-3 pb-0 flex flex-col"
      style={{ height }}
    >
      <p className="text-xs font-medium text-gray-500 mb-2 shrink-0">{label}</p>
      <div className="flex-1 min-h-0">{children}</div>
      {/* drag handle */}
      <div
        onMouseDown={onMouseDown}
        className="shrink-0 flex items-center justify-center h-4 cursor-row-resize select-none"
      >
        <div className="w-8 h-1 rounded-full bg-gray-200 hover:bg-gray-400 transition-colors" />
      </div>
    </div>
  );
};

const GROUP_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

const toLocalDatetimeValue = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const defaultEnd = () => toLocalDatetimeValue(new Date());
const defaultStart = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return toLocalDatetimeValue(d);
};

const formatCurrency = (val) => {
  if (val == null || Number.isNaN(val)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
};


export const ExecutiveAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [groupBy, setGroupBy] = useState("monthly");
  const [rows, setRows] = useState([]);

  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);
  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("success");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startIso = start ? new Date(start).toISOString() : null;
      const endIso = end ? new Date(end).toISOString() : null;
      const res = await fetchExecutiveAnalytics(groupBy, 36, startIso, endIso);
      if (res.success && res.data?.rows) {
        setRows(res.data.rows);
      } else {
        setRows([]);
        setToastMessage(res.message || "Failed to load executive analytics");
        setToastType("error");
      }
    } catch (err) {
      console.error("Error fetching executive analytics:", err);
      setRows([]);
      setToastMessage("Error loading executive analytics");
      setToastType("error");
    } finally {
      setLoading(false);
    }
  }, [groupBy, start, end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const shortenLabel = useCallback(
    (label) => {
      if (groupBy === "weekly") {
        // "Week of Oct 21, 2026" → "Oct 21"
        const parts = label.split(" ");
        return parts.length >= 4 ? `${parts[2]} ${parts[3].replace(",", "")}` : label;
      }
      if (groupBy === "daily") {
        // "Oct 21, 2026" → "Oct 21"
        const parts = label.split(" ");
        return parts.length >= 2 ? `${parts[0]} ${parts[1].replace(",", "")}` : label;
      }
      return label;
    },
    [groupBy]
  );

  const display = useMemo(() => [...rows].reverse().slice(-12), [rows]);

  const chartData = useMemo(() => ({
    labels: display.map((r) => r.period_label),
    datasets: [
      {
        label: "Revenue (₹)",
        data: display.map((r) => r.revenue),
        backgroundColor: "#10b981",
        borderRadius: 4,
      },
    ],
  }), [display]);

  const stackedNewRenewData = useMemo(() => ({
    labels: display.map((r) => r.period_label),
    datasets: [
      {
        label: "New",
        data: display.map((r) => r.new_subscriptions_starting),
        backgroundColor: "#6366f1",
        borderRadius: 2,
      },
      {
        label: "Renewals",
        data: display.map((r) => r.renewals),
        backgroundColor: "#f59e0b",
        borderRadius: 2,
      },
    ],
  }), [display]);

  const planNames = useMemo(
    () => [...new Set(rows.flatMap((r) => Object.keys(r.active_by_plan ?? {})))].sort(),
    [rows]
  );

  const PLAN_COLORS = ["#3b82f6", "#8b5cf6", "#ec4899", "#f97316", "#14b8a6", "#84cc16"];

  const stackedByPlanData = useMemo(() => ({
    labels: display.map((r) => r.period_label),
    datasets: planNames.map((name, i) => ({
      label: name,
      data: display.map((r) => r.active_by_plan?.[name] ?? 0),
      backgroundColor: PLAN_COLORS[i % PLAN_COLORS.length],
      borderRadius: 2,
    })),
  }), [display, planNames]);

  const xTickConfig = useMemo(() => {
    if (groupBy === "monthly") return { grid: { display: false } };
    return {
      grid: { display: false },
      ticks: {
        maxRotation: 45,
        minRotation: 45,
        callback(val, idx) {
          return shortenLabel(this.getLabelForValue(idx));
        },
      },
    };
  }, [groupBy, shortenLabel]);

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: { label: (ctx) => formatCurrency(ctx.parsed.y) },
        },
      },
      scales: {
        y: {
          ticks: { callback: (v) => `₹${(v / 1000).toFixed(0)}k` },
          grid: { color: "#f3f4f6" },
        },
        x: xTickConfig,
      },
    }),
    [xTickConfig]
  );

  const stackedOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: "top", labels: { boxWidth: 10, font: { size: 10 } } },
        tooltip: {
          callbacks: {
            footer: (items) => `Total: ${items.reduce((s, i) => s + i.parsed.y, 0)}`,
          },
        },
      },
      scales: {
        x: { ...xTickConfig, stacked: true },
        y: { stacked: true, grid: { color: "#f3f4f6" } },
      },
    }),
    [xTickConfig]
  );

  return (
    <div className="flex-1 flex flex-col min-h-0 w-full">
      {loading ? (
        <Loader />
      ) : (
        <div className="container max-w-7xl px-4 sm:px-6 py-6 pb-16 mx-auto">
          <div className="mb-5">
            <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
              Executive analytics
            </h1>
            <div className="mt-1.5 inline-flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
              <span>
                <strong className="text-gray-700">Revenue & Paid Subs</strong> — payment date
              </span>
              <span>
                <strong className="text-gray-700">New Clients, Renewals & Active Subs</strong> —
                plan start date
              </span>
              <span>
                <strong className="text-gray-700">Downloads</strong> — sign-up date
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {/* Controls row */}
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="datetime-local"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
              <span className="text-gray-400 text-sm">–</span>
              <input
                type="datetime-local"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-400"
              />
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value)}
                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white"
              >
                {GROUP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                onClick={() => { setStart(defaultStart()); setEnd(defaultEnd()); setGroupBy("monthly"); }}
                className="text-xs text-gray-500 hover:text-gray-800 underline"
              >
                Reset
              </button>
            </div>

            {/* Revenue trend chart */}
            {rows.length > 0 && (
              <ResizableChart
                label={`Revenue trend · ${Math.min(rows.length, 12)} ${groupBy} periods · bucketed by payment date`}
                minHeight={220}
              >
                <Bar data={chartData} options={chartOptions} />
              </ResizableChart>
            )}

            {/* New clients vs renewals chart */}
            {rows.length > 0 && (
              <ResizableChart label="New clients vs renewals · by plan start date">
                <Bar data={stackedNewRenewData} options={stackedOptions} />
              </ResizableChart>
            )}

            {/* Active subscriptions by plan chart */}
            {rows.length > 0 && (
              <ResizableChart label="Active subscriptions by plan · plan start/end overlap">
                <Bar data={stackedByPlanData} options={stackedOptions} />
              </ResizableChart>
            )}

            {rows.length === 0 && !loading && (
              <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-xl border border-gray-200">
                <div className="rounded-full bg-gray-100 p-4 mb-4">
                  <TrendingUp className="w-12 h-12 text-gray-400" />
                </div>
                <p className="text-base font-medium text-gray-700">No data for this period</p>
                <p className="text-sm text-gray-500 mt-1 max-w-sm text-center">
                  Adjust the date range or frequency to see metrics.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {toastMessage && <Toaster message={toastMessage} type={toastType} />}
    </div>
  );
};

export default ExecutiveAnalyticsDashboard;
